import { useState } from "react";
import { Link } from "react-router-dom";
import { Radio, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { authAPI } from "@/lib/api";

export default function LoginPage({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    email: "",
    password: "",
    name: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let response;
      if (isRegister) {
        response = await authAPI.register({
          email: form.email,
          password: form.password,
          name: form.name,
          role: "technician",
        });
        toast.success("Cuenta creada exitosamente");
      } else {
        response = await authAPI.login({
          email: form.email,
          password: form.password,
        });
        toast.success("Bienvenido a AVX RF PRO");
      }
      onLogin(response.data.user, response.data.access_token);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || "Error de autenticación";
      // Translate common error messages to Spanish
      const translatedMessage = errorMessage === "Email already registered" 
        ? "Este correo ya está registrado" 
        : errorMessage;
      toast.error(translatedMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Left side - Image */}
      <div
        className="login-image hidden lg:block"
        style={{
          backgroundImage:
            "url('https://images.pexels.com/photos/4889280/pexels-photo-4889280.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 to-black/60 z-10" />
        <div className="relative z-20 h-full flex flex-col justify-end p-12">
          <div className="max-w-md">
            <h2 className="font-heading text-3xl font-bold text-white mb-4">
              Control RF Profesional
            </h2>
            <p className="text-[#A1A1AA] text-lg">
              Monitoreo y optimización de frecuencias para sistemas In-Ear.
              Diseñado para eventos en vivo, iglesias y producción audiovisual.
            </p>
          </div>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="login-form-container">
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-sm bg-[#34C759] flex items-center justify-center">
              <Radio className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-xl text-[#F4F4F5]">
                AVX RF PRO
              </h1>
              <p className="text-xs text-[#71717A] tracking-widest">
                ANLEON CHILE
              </p>
            </div>
          </div>

          <h2 className="font-heading text-2xl font-bold text-[#F4F4F5] mb-2">
            {isRegister ? "Crear Cuenta" : "Iniciar Sesión"}
          </h2>
          <p className="text-[#71717A]">
            {isRegister
              ? "Registra tu cuenta para comenzar"
              : "Ingresa tus credenciales para continuar"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {isRegister && (
            <div className="form-group">
              <Label htmlFor="name" className="form-label">
                Nombre
              </Label>
              <Input
                id="name"
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="input-tactical w-full"
                placeholder="Tu nombre"
                required={isRegister}
                data-testid="register-name-input"
              />
            </div>
          )}

          <div className="form-group">
            <Label htmlFor="email" className="form-label">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="input-tactical w-full"
              placeholder="tu@email.com"
              required
              data-testid="login-email-input"
            />
          </div>

          <div className="form-group">
            <Label htmlFor="password" className="form-label">
              Contraseña
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="input-tactical w-full pr-10"
                placeholder="••••••••"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#F4F4F5]"
              >
                {showPassword ? (
                  <EyeOff className="w-4 h-4" />
                ) : (
                  <Eye className="w-4 h-4" />
                )}
              </button>
            </div>
          </div>

          {/* Forgot Password Link - Only show on login mode */}
          {!isRegister && (
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-sm text-[#34C759] hover:text-[#34C759]/80 transition-colors"
                data-testid="forgot-password-link"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          )}

          <Button
            type="submit"
            className="btn-tactical btn-tactical-primary w-full"
            disabled={loading}
            data-testid="login-submit-button"
          >
            {loading
              ? "Procesando..."
              : isRegister
              ? "Crear Cuenta"
              : "Iniciar Sesión"}
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-[#71717A]">
            {isRegister ? "¿Ya tienes cuenta?" : "¿No tienes cuenta?"}{" "}
            <button
              onClick={() => setIsRegister(!isRegister)}
              className="text-[#34C759] hover:underline font-medium"
              data-testid="toggle-auth-mode"
            >
              {isRegister ? "Iniciar Sesión" : "Registrarse"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
